import { useState, useEffect, useCallback, useRef } from 'react';
import { workflowService } from '../../services/workflowService';
import {
  BatchWorkflowListItem,
  BatchWorkflowListRequest,
} from '../../types/workflow';
import type { WorkflowFilters } from './useWorkflowFilters';
import useDebouncedValue from '../useDebouncedValue';
import { format } from 'date-fns';
import { computeDateRange } from '../../utils/dateRangeFilter';

/**
 * Classified error with retry information.
 */
interface ClassifiedError {
  message: string;
  retryable: boolean;
}

/**
 * Classifies an error and determines if it's retryable.
 */
const classifyError = (err: unknown): ClassifiedError => {
  if (err instanceof Error) {
    // Auth errors are not retryable
    if (err.message.includes('401') || err.message.includes('unauthorized')) {
      return { message: 'Session expired. Please log in again.', retryable: false };
    }
    // Network/server errors are retryable
    if (err.message.includes('network') || err.message.includes('500')) {
      return { message: 'Server error. Please try again.', retryable: true };
    }
    return { message: err.message, retryable: true };
  }
  return { message: 'An unexpected error occurred', retryable: true };
};

/**
 * Return type for the useWorkflowBatches hook.
 */
export interface UseWorkflowBatchesReturn {
  /** Array of batch workflow items */
  batches: BatchWorkflowListItem[];
  /** Total number of items matching the current filters */
  totalCount: number;
  /** Total number of pages based on pageSize */
  totalPages: number;
  /** Whether a fetch is in progress */
  loading: boolean;
  /** Classified error if the fetch failed, null otherwise */
  error: ClassifiedError | null;
  /** Function to manually trigger a refetch */
  refetch: () => void;
}

/**
 * Custom hook for fetching batch workflow data with server-side pagination and filtering.
 *
 * Single Responsibility: ONLY handles batch data fetching.
 * - Fetches batches based on provided filters
 * - Manages loading and error states
 * - Implements request cancellation via AbortController for rapid filter changes
 * - Provides refetch capability
 *
 * Dependency Inversion: Depends on workflowService abstraction, not direct API calls.
 *
 * @param filters - The filter state controlling pagination, sorting, and filtering
 * @returns Object containing batches, totalCount, totalPages, loading, error, and refetch function
 *
 * @example
 * ```tsx
 * const { batches, loading, error, refetch } = useWorkflowBatches(filters);
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 * return <BatchList batches={batches} />;
 * ```
 */
export function useWorkflowBatches(filters: WorkflowFilters): UseWorkflowBatchesReturn {
  const [batches, setBatches] = useState<BatchWorkflowListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ClassifiedError | null>(null);

  // Track current request to prevent stale updates
  // AbortController allows us to cancel in-flight requests when filters change rapidly
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce the free-text search so typing fires one request, not one per keystroke
  // (the other filters are discrete clicks and fetch immediately).
  const debouncedSearch = useDebouncedValue(filters.search);

  /**
   * Fetches batches from the API with the current filters.
   * Supports request cancellation via AbortSignal.
   */
  const fetchBatches = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    try {
      // Resolve the created-date preset (or custom range) into concrete bounds at request time so
      // "Last N days" is always relative to now.
      const { from: createdFrom, to: createdTo } = computeDateRange(
        filters.datePreset,
        filters.dateFrom,
        filters.dateTo,
      );

      // Build the request object from filters
      // Only include non-empty values to avoid sending undefined params
      const request: BatchWorkflowListRequest = {
        pageNumber: filters.page,
        pageSize: filters.pageSize,
        statusCode: filters.statusCode || undefined,
        priority: filters.priority || undefined,
        isOverdue: filters.overdueOnly || undefined,
        includeCompleted: filters.showCompleted || undefined,
        searchTerm: debouncedSearch || undefined,
        sampleType: filters.sampleType || undefined,
        createdAfter: createdFrom ? format(createdFrom, 'yyyy-MM-dd') : undefined,
        createdBefore: createdTo ? format(createdTo, 'yyyy-MM-dd') : undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      const response = await workflowService.getBatches(request, signal);

      // Only update state if request wasn't aborted
      // This prevents stale data from overwriting newer data
      if (!signal?.aborted) {
        setBatches(response.items);
        setTotalCount(response.totalCount);
      }
    } catch (err: unknown) {
      // Ignore abort errors - they're intentional when filters change rapidly
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // Also check for axios CanceledError which wraps AbortError
      if (err instanceof Error && err.name === 'CanceledError') {
        return;
      }

      // Only update error state if request wasn't aborted
      if (!signal?.aborted) {
        setError(classifyError(err));
      }
    } finally {
      // Only update loading state if request wasn't aborted
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [
    filters.page,
    filters.pageSize,
    filters.statusCode,
    filters.priority,
    filters.overdueOnly,
    filters.showCompleted,
    filters.sampleType,
    filters.datePreset,
    filters.dateFrom,
    filters.dateTo,
    debouncedSearch,
    filters.sortBy,
    filters.sortOrder,
  ]);

  // Effect with request cancellation for rapid filter changes
  useEffect(() => {
    // Cancel any in-flight request from previous filter state
    abortControllerRef.current?.abort();

    // Create new controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- data-fetch effect intentionally sets loading state synchronously at request start
    fetchBatches(controller.signal);

    // Cleanup: cancel request if filters change or component unmounts
    return () => controller.abort();
  }, [fetchBatches]);

  // Calculate total pages based on totalCount and pageSize
  const totalPages = Math.ceil(totalCount / filters.pageSize);

  /**
   * Manual refetch function - user initiated.
   * Cancels any in-flight request and starts a new one.
   */
  const refetch = useCallback(() => {
    // Cancel any existing request
    abortControllerRef.current?.abort();

    // Create new controller for the refetch
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchBatches(controller.signal);
  }, [fetchBatches]);

  return {
    batches,
    totalCount,
    totalPages,
    loading,
    error,
    refetch,
  };
}
